import React, { createContext, useContext, useEffect, useState } from 'react'
import * as Sentry from '@sentry/react'

import AuthService from '../../services/AuthService'
import useSessionRefresh from '../../hooks/useSessionRefresh'

import { ToastsContext } from './ToastsContext.jsx'

export const UserContext = createContext(null)

const UserContextProvider = ({ children }) => {
  const [user, setUser] = useState(false)
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [loading, setLoading] = useState(true)

  const [selectedDB, setSelectedDB] = useState(() => {
    // Don't restore from localStorage - always use server data
    return ''
  })
  const [listOfUsers, setListOfUsers] = useState([])
  const [enabledModules, setEnabledModules] = useState([])

  const { setToasts } = useContext(ToastsContext)

  useEffect(() => {
    const checkTokenValidity = async () => {
      // Non si guarda piu' se il cookie c'e': ora e' HttpOnly e da qui non si
      // vede. A dire se la sessione e' viva e' il backend, che il cookie ce
      // l'ha davvero — un 401 e' l'unica risposta che vale.
      try {
        const response = await AuthService.verifyToken()
        setIsAuthenticated(true)
        const user = response.data.user
        const currentUserEmail = user.email

        // Get previous user email from sessionStorage (stesso storage di
        // Login.jsx — NON localStorage, per privacy su device condivisi e
        // perché due storage diversi per lo stesso stato facevano fallire
        // per sempre questo confronto, con reload infinito: incidente 2026-08-03).
        const previousUserEmail = sessionStorage.getItem('currentUserEmail')

        // Check if this is a different user than before
        if (previousUserEmail && previousUserEmail !== currentUserEmail) {
          // Different user - clear all caches and hard refresh
          console.log(
            `User changed from ${previousUserEmail} to ${currentUserEmail}. Clearing caches and performing hard refresh...`
          )
          localStorage.clear()
          sessionStorage.clear()
          window.location.reload()
          return
        }

        // Update current user email in sessionStorage
        sessionStorage.setItem('currentUserEmail', currentUserEmail)

        // Set user object
        setUser(user)

        // Determine the primary database for the user
        // Priority: 1) Non-default database (not 'wiccaDATA'), 2) First available database, 3) 'wiccaDATA' only if it's the only one
        if (
          user.database_access &&
          Array.isArray(user.database_access) &&
          user.database_access.length > 0
        ) {
          // Filter out any null, undefined, or empty values
          const validDatabases = user.database_access.filter(
            (db) => db && typeof db === 'string' && db.trim() !== ''
          )

          if (validDatabases.length === 0) {
            console.error(
              `[UserContext] User ${currentUserEmail} has no valid databases in database_access!`
            )
            setSelectedDB('')
            return
          }

          let primaryDB = null

          // If user has only one database, use it regardless of name
          if (validDatabases.length === 1) {
            primaryDB = validDatabases[0]
          } else {
            // User has multiple databases - prioritize non-default database
            // First, try to find a non-default database (not 'wiccaDATA')
            const nonDefaultDB = validDatabases.find((db) => db !== 'wiccaDATA')

            if (nonDefaultDB) {
              // User has a non-default database, use that
              primaryDB = nonDefaultDB
            } else {
              // User only has 'wiccaDATA' or default databases, use the first one
              primaryDB = validDatabases[0]
            }
          }

          // Force set the selected database - use functional update to ensure it's set
          setSelectedDB(() => primaryDB)
        } else {
          console.error(
            `[UserContext] ❌ ERROR - User ${currentUserEmail} has no database_access assigned! User object:`,
            JSON.stringify(user, null, 2)
          )
          setSelectedDB('')
        }

        Sentry.setUser({
          email: user.email,
        })
        const res = await AuthService.getListOfAccounts()
        setListOfUsers(res.data.accounts)

        // Fetch enabled modules for the user
        try {
          const modulesRes = await AuthService.getUserModules()
          setEnabledModules(modulesRes.data.enabled_modules || [])
        } catch (moduleErr) {
          console.warn(
            'Failed to fetch user modules, using defaults:',
            moduleErr
          )
          // Fallback to all modules for backward compatibility
          setEnabledModules(['segmentation', 'engineering', 'respirology', 'vascular'])
        }
      } catch (e) {
        if (e.response?.status === 401) {
          // Il cookie lo toglie il backend: da qui si segnala e basta.
          setToasts([e.response.data?.message].filter(Boolean))
        }
        setIsAuthenticated(false)
        // Clear user email from sessionStorage on authentication error
        sessionStorage.removeItem('currentUserEmail')
      } finally {
        setLoading(false)
      }
    }

    checkTokenValidity()
    // Una volta sola: ogni ingresso (login, MFA, callback OAuth) finisce con
    // un location.replace, quindi il provider si rimonta da zero.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Il biglietto scade dopo un'ora: qui dentro sappiamo se c'e' davvero
  // qualcuno a cui rinnovarlo.
  useSessionRefresh(isAuthenticated)

  return (
    <UserContext.Provider
      value={{
        user,
        setUser,
        isAuthenticated,
        loading,
        selectedDB,
        setSelectedDB,
        listOfUsers,
        setListOfUsers,
        enabledModules,
        setEnabledModules,
      }}
    >
      {children}
    </UserContext.Provider>
  )
}

export default UserContextProvider
